import React, { useState } from "react";
import ArbitrageTicker from "@/components/ArbitrageTicker";
import { useGlobalData } from "@/lib/dataStreams";
import { ArrowsLeftRight, CaretDown } from "@phosphor-icons/react";

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-800 group bg-slate-900/20 hover:bg-slate-900/40 transition-colors px-6 rounded-2xl mb-4">
      <button 
        onClick={() => setOpen(!open)} 
        className="w-full py-6 flex justify-between items-center text-left"
      >
        <span className="font-bold text-lg text-slate-200 group-hover:text-indigo-400 transition-colors pr-4">{q}</span>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors ${open ? 'bg-indigo-500/20 text-indigo-400' : 'bg-slate-800 text-slate-400'}`}>
          <CaretDown size={16} className={`transform transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${open ? 'max-h-96 opacity-100 pb-6' : 'max-h-0 opacity-0'}`}>
        <p className="text-slate-300 leading-relaxed text-sm sm:text-base pr-8">
          {a}
        </p>
      </div>
    </div>
  );
};

function SpreadMatrix({ ticker }) {
  return (
    <>
      <h3 className="font-bold text-sm uppercase tracking-wider text-slate-400 mb-4">Live Spread Matrix (Real CEX Quotes)</h3>
      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden mb-12 shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left whitespace-nowrap">
            <thead>
              <tr className="border-b border-slate-800 bg-slate-950/50 text-slate-400 text-xs uppercase tracking-wider font-bold">
                <th className="p-5">Buy Venue</th>
                <th className="p-5">Sell Venue</th>
                <th className="p-5">Asset</th>
                <th className="p-5">Spread</th>
                <th className="p-5">Est. PnL</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {ticker.length === 0 ? (
                <tr><td colSpan="5" className="p-8 text-center text-slate-500 text-xs">Waiting for data...</td></tr>
              ) : (
                ticker.map((t) => (
                  <tr key={t.id} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                    <td className="p-5 text-slate-200">{t.buy_ex} <span className="text-slate-500 text-xs">(${t.buy_price})</span></td>
                    <td className="p-5 text-slate-200">{t.sell_ex} <span className="text-slate-500 text-xs">(${t.sell_price})</span></td>
                    <td className="p-5 text-slate-400">{t.pair}</td>
                    <td className="p-5 text-indigo-400 font-bold">+{t.spread_pct}%</td>
                    <td className="p-5 text-emerald-400 font-bold">+{t.pnl_usd} USD</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function NetworkSwaps({ solSwaps }) {
  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-sm uppercase tracking-wider text-slate-400 flex items-center gap-2">
          <ArrowsLeftRight size={18} className="text-indigo-400" /> Live Arbitrage Executions
        </h3>
        <div className="flex items-center gap-2 bg-indigo-500/10 px-3 py-1.5 rounded-full border border-indigo-500/20">
          <span className="w-2 h-2 bg-indigo-400 rounded-full animate-pulse shadow-[0_0_8px_rgba(99,102,241,0.8)]" />
          <span className="text-indigo-400 text-[10px] font-bold tracking-wider uppercase">Streaming Real Txns</span>
        </div>
      </div>
      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left whitespace-nowrap">
            <thead>
              <tr className="border-b border-slate-800 bg-slate-950/50 text-slate-400 text-xs uppercase tracking-wider font-bold">
                <th className="p-5">Time (UTC)</th>
                <th className="p-5">Route</th>
                <th className="p-5">Swap</th>
                <th className="p-5">Size (SOL)</th>
                <th className="p-5">Value (USD)</th>
                <th className="p-5">Est. PnL</th>
                <th className="p-5">Status</th>
                <th className="p-5 text-right">Transaction Hash</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {!solSwaps || solSwaps.length === 0 ? (
                <tr><td colSpan="8" className="p-8 text-center text-slate-500 text-xs animate-pulse">Scanning Solana Mainnet...</td></tr>
              ) : (
                solSwaps.map((s) => {
                  const stable = s.stableToken || "USDC";
                  const inProgress = s.status === "IN_PROGRESS" || !s.status;
                  const failed = s.status === "FAILED";
                  return (
                    <tr key={s.signature} className={`border-b border-slate-800/50 hover:bg-slate-800/30 transition-opacity duration-1000 ${s.vanishing ? "opacity-0" : "opacity-100"}`}>
                      <td className="p-5 text-slate-400 text-[11px] tracking-wider">
                        {new Date(s.ts).toLocaleTimeString([], { hour12: false, timeZone: "UTC" })}
                      </td>
                      <td className="p-5 text-slate-300 text-xs font-semibold">
                        {s.isBuy ? `${s.dex} → ${s.cex || "Binance"}` : `${s.cex || "Binance"} → ${s.dex}`}
                      </td>
                      <td className="p-5">
                        <span className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-md ${s.isBuy ? "bg-emerald-500/10 text-emerald-400" : "bg-rose-500/10 text-rose-400"}`}>
                          {s.isBuy ? `${stable} → SOL` : `SOL → ${stable}`}
                        </span>
                      </td>
                      <td className="p-5 text-slate-200 font-bold">{s.solAmount.toFixed(2)}</td>
                      <td className="p-5 text-slate-400">${s.usdcAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="p-5 font-bold">
                        {inProgress ? (
                          <span className="text-slate-500 animate-pulse">calculating…</span>
                        ) : failed ? (
                          <span className="text-slate-600">—</span>
                        ) : (
                          <span className="text-emerald-400">
                            +${Math.abs(s.pnl || 0).toFixed(2)}
                          </span>
                        )}
                      </td>
                      <td className="p-5">
                        {inProgress ? (
                          <span className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-md bg-amber-500/10 text-amber-400 animate-pulse">In Progress</span>
                        ) : failed ? (
                          <span className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-md bg-rose-500/10 text-rose-400">Failed</span>
                        ) : (
                          <span className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-md bg-emerald-500/10 text-emerald-400">Success</span>
                        )}
                      </td>
                      <td className="p-5 text-right text-slate-500 text-xs truncate max-w-[150px]">
                        <a href={`https://solscan.io/tx/${s.signature}`} target="_blank" rel="noreferrer" className="hover:text-indigo-400 transition-colors underline decoration-slate-700 underline-offset-4">
                          {s.signature}
                        </a>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

export default function ArbitrageBot() {
  const { ticker, solPrice: price, solSwaps } = useGlobalData();

  const faqs = [
    {
      q: "What exchanges are supported in the Arbitrage module?",
      a: "The module monitors 11 major CEXs (Binance, Coinbase, Kraken, OKX, Gate.io, MEXC, Kucoin, Bitfinex, Bitstamp, Gemini, HTX) alongside primary Solana DEXs (Orca, Raydium, Meteora, Phoenix). This cross-chain analysis ensures we have full visibility on all available liquidity for SOL/USDC and SOL/USDT pairs."
    },
    {
      q: "How does the bot prevent MEV sandwich attacks?",
      a: "By leveraging Jito bundles. Rather than broadcasting our arbitrage transactions to the public mempool where searchers can front-run us, we bundle our sequence of trades (Buy DEX -> Transfer -> Sell CEX equivalents) and send them directly to a Jito-running validator along with a SOL tip. This ensures the transaction executes exactly as ordered, invisibly."
    },
    {
      q: "What happens if a price spread disappears mid-execution?",
      a: "The smart contract is designed atomically. We utilize Solana's composability to wrap the entire arbitrage loop into a single transaction. If the price on the destination exchange shifts and the transaction calculates a net loss or a spread below our minimum threshold (0.05%), the entire transaction simply reverts. The only cost incurred is the microscopic base network fee (less than $0.001)."
    },
    {
      q: "How fast does this execute?",
      a: "Our infrastructure takes full advantage of Solana's 400ms block time. We co-locate our private RPC nodes near major validators in Tokyo and Amsterdam, bringing our total round-trip latency to under 400ms. This allows us to capture micro-inefficiencies before the broader market reacts."
    },
    {
      q: "Why do price differences between exchanges exist at all?",
      a: "Every exchange is its own isolated market with its own buyers and sellers. A large market sell on Binance pushes its SOL price down instantly, but Coinbase's order book doesn't feel that pressure for several seconds. Liquidity depth, regional demand, fee tiers, and even fiat on-ramp flows all differ per venue — so prices are in a constant state of micro-divergence. Arbitrage is the force that pulls them back together."
    },
    {
      q: "Is arbitrage risk-free?",
      a: "It is market-neutral, not risk-free. The strategy never bets on price direction, which removes directional risk entirely. What remains is execution risk: the spread can close in the milliseconds between detection and settlement, a leg can fail, or network congestion can delay inclusion. Our atomic transaction design converts almost all of that residual risk into a simple reverted transaction costing a fraction of a cent."
    },
    {
      q: "What is a basis point and why does it matter here?",
      a: "A basis point (bp) is one hundredth of one percent (0.01%). Cross-exchange SOL spreads typically live in the 1-10 bp range. That sounds tiny, but a 5 bp spread captured on a $50,000 rotation is $25 of profit with zero market exposure — and the opportunity repeats hundreds of times per day. Arbitrage is a volume game measured in basis points, not a jackpot game."
    },
    {
      q: "What's the difference between CEX-DEX and DEX-DEX arbitrage?",
      a: "DEX-DEX arbitrage happens entirely on-chain (e.g., Raydium vs Orca) and can be executed atomically in a single transaction — both legs succeed or neither does. CEX-DEX arbitrage compares an on-chain pool against a centralized order book like Binance; it captures larger spreads because the two systems desync more, but it requires pre-positioned inventory on both venues since funds can't teleport between them mid-trade."
    },
    {
      q: "Why does the bot pre-position inventory on exchanges?",
      a: "Moving funds between a CEX and the blockchain takes minutes — an eternity in arbitrage. Instead, the bot holds SOL and USDC balances on every monitored venue simultaneously. When a spread appears, it simply buys on the cheap venue and sells on the expensive one at the same instant, then periodically rebalances inventory when the books drift too far to one side."
    },
    {
      q: "How do trading fees affect arbitrage profitability?",
      a: "Fees are the primary filter. A 5 bp spread is worthless if you pay 10 bp in taker fees to capture it. The engine maintains a live fee model per venue — maker/taker tiers, Solana priority fees, and DEX LP fees — and only fires when the net spread after all costs clears the minimum profit threshold. This is why high-volume fee tiers and Solana's sub-cent transaction costs are structural advantages."
    },
    {
      q: "What is slippage and how is it controlled?",
      a: "Slippage is the difference between the price you expect and the price you actually fill at, caused by your own order consuming the order book. The engine sizes every trade against live liquidity depth — it will only take the portion of the spread that the top of the book can absorb. On-chain legs additionally carry a hard slippage limit; if the pool moves beyond it before execution, the transaction reverts rather than filling badly."
    },
    {
      q: "Why is Solana particularly suited for arbitrage?",
      a: "Three reasons: 400ms block times mean on-chain prices update and settle faster than on any other major chain; transaction fees are fractions of a cent, so even 2-3 bp spreads are profitable; and Solana's atomic composability lets an entire multi-leg arbitrage route execute as one all-or-nothing transaction. On slower chains the same strategy would be eaten alive by gas costs and settlement risk."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans selection:bg-indigo-500/30 pb-20">
      <ArbitrageTicker />
      <div className="max-w-[1500px] mx-auto px-4 md:px-8 py-12">
        <div className="mb-12 border-b border-slate-800 pb-8">
          <span className="text-indigo-400 font-bold text-xs uppercase tracking-wider">// Module 01</span>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mt-3 text-slate-100">
            Arbitrage
          </h1>
          <p className="text-slate-300 mt-5 max-w-3xl text-base leading-relaxed">
            This module monitors primary centralized exchanges alongside Solana decentralized exchanges to identify pricing inefficiencies. By calculating the real-time spread and factoring in network fees, it executes atomic routing to secure basis point spreads without market exposure.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-sm">
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Exchanges Scanned</div>
            <div className="font-display font-extrabold text-4xl text-slate-100">11 <span className="text-lg text-slate-500 font-medium">CEX/DEX</span></div>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-sm">
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Base Reference (SOL)</div>
            <div className="font-mono font-bold text-4xl text-indigo-400">{price ? `$${price.toFixed(3)}` : "—"}</div>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-sm">
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Execution Speed</div>
            <div className="font-mono font-bold text-4xl text-emerald-400">~400<span className="text-lg text-slate-500 font-medium">ms</span></div>
          </div>
        </div>

        <SpreadMatrix ticker={ticker} />
        <NetworkSwaps solSwaps={solSwaps} />

        {/* EDUCATION SECTION */}
        <div className="mt-24 max-w-[1100px] mx-auto">
          <div className="text-center mb-12">
            <span className="text-indigo-400 font-bold text-xs uppercase tracking-wider bg-indigo-500/10 px-3 py-1.5 rounded-full border border-indigo-500/20">// Deep Dive</span>
            <h2 className="text-4xl font-extrabold text-slate-100 mt-5 mb-3">What Is Arbitrage?</h2>
            <p className="text-slate-300 max-w-2xl mx-auto leading-relaxed">
              The oldest strategy in finance, rebuilt for the fastest blockchain in the world.
            </p>
          </div>

          <div className="space-y-6">
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-sm">
              <h3 className="text-xl font-bold text-slate-100 mb-3">The Same Asset, Two Prices</h3>
              <p className="text-slate-300 leading-relaxed text-sm sm:text-base">
                Arbitrage is the practice of buying an asset on one market and simultaneously selling it on another where it trades at a higher price — pocketing the difference with no bet on which way the market moves. SOL trades on dozens of venues at once: centralized exchanges like Binance, Coinbase, and Kraken, and on-chain pools like Raydium, Orca, and Phoenix. Each venue is its own island of supply and demand, so their prices constantly drift apart by fractions of a percent. Those fractions are the entire business model of an arbitrage bot.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-sm">
                <h3 className="text-lg font-bold text-slate-100 mb-3">Market-Neutral by Construction</h3>
                <p className="text-slate-300 leading-relaxed text-sm">
                  A momentum trader profits only if price goes up. An arbitrageur is long and short the same asset at the same instant, so whether SOL rallies 10% or crashes 10% during the trade is irrelevant — the profit is locked in the spread itself. This is why arbitrage desks are the steadiest earners in traditional finance, and why the Synergy Engine treats this module as its low-risk ballast rather than its growth engine.
                </p>
              </div>
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-sm">
                <h3 className="text-lg font-bold text-slate-100 mb-3">A Game of Basis Points</h3>
                <p className="text-slate-300 leading-relaxed text-sm">
                  Real spreads are small — typically 1 to 10 basis points (0.01%–0.1%). Nobody retires on one arbitrage trade. The edge comes from repetition: capturing a few basis points, hundreds of times a day, with near-zero downside per attempt. Speed, fee discipline, and uptime matter far more than any single opportunity. The bots that win are the ones that are simply present for every spread, every hour, without fatigue.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-sm">
                <h3 className="text-lg font-bold text-slate-100 mb-3">Atomic Execution: All or Nothing</h3>
                <p className="text-slate-300 leading-relaxed text-sm">
                  The classic arbitrage nightmare is "legging risk" — your buy fills but your sell doesn't, leaving you holding directional exposure you never wanted. Solana's composability eliminates this on-chain: both legs of a DEX-DEX arbitrage are wrapped into a single atomic transaction. If the spread closes mid-flight, the whole transaction reverts and the only cost is a network fee smaller than a cent. You'll see this in the live log above — failed attempts simply revert and disappear.
                </p>
              </div>
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-sm">
                <h3 className="text-lg font-bold text-slate-100 mb-3">The Latency Arms Race</h3>
                <p className="text-slate-300 leading-relaxed text-sm">
                  Every spread is visible to every bot at roughly the same moment — the fastest one gets the fill and the rest get nothing. That's why serious operations co-locate RPC nodes next to validators, stream order books over websockets instead of polling, and pre-compute transaction templates so the moment a spread crosses threshold, a signed transaction is already on the wire. In this game, milliseconds are the moat.
                </p>
              </div>
            </div>

            <div className="bg-indigo-500/5 border border-indigo-500/20 rounded-2xl p-8 shadow-sm">
              <h3 className="text-xl font-bold text-slate-100 mb-3">Why Arbitrage Matters for the Market</h3>
              <p className="text-slate-300 leading-relaxed text-sm sm:text-base">
                Arbitrage isn't just extraction — it's the mechanism that keeps global prices honest. Every time the bot buys on the cheap venue and sells on the expensive one, it nudges both prices toward each other. The result is that a trader in any country, on any exchange, sees roughly the same SOL price at any moment. Regulators and academics call this "price discovery efficiency"; arbitrageurs are the unpaid janitors of it. The tighter the spreads you see in the matrix above, the healthier the market is — and the harder the remaining basis points are to win.
              </p>
            </div>
          </div>
        </div>

        {/* FAQ SECTION */}
        <div className="mt-20 max-w-[900px] mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-extrabold text-slate-100 mb-2">Arbitrage Knowledge Base</h2>
            <p className="text-slate-300">Technical insights into the arbitrage engine.</p>
          </div>
          <div className="space-y-2">
            {faqs.map((faq, i) => (
              <FaqItem key={i} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}
